/**
 * src/config/websiteCaptureLaunch.js
 *
 * WEBSITE CAPTURE SUITE LAUNCH STATE - server-authoritative, fail closed.
 *
 * Website Capture Suite is an ACTIVE paid VISINVOKE product, but it is not yet
 * commercially on sale. VISINVOKE deliberately shows what is coming (the price
 * and product stay visible) and there is no working purchase path until the
 * owner explicitly enables sales on the server.
 *
 * The server decides. This module only reads GET /v1/website-capture/commerce
 * and hands the answer to the surfaces that present the product, so no screen
 * carries its own opinion about whether Website Capture Suite is on sale.
 *
 * Fail closed on every branch: while loading, on a network error, on a
 * malformed payload, or on any missing field, `purchasable` is false and the
 * customer sees the pre-launch state. Configuring a Stripe price id cannot open
 * the path - the server gate is separate, and this reads the gate.
 */
import { useEffect, useState } from 'react';
import API from '../api';

// The state used before the server answers, and whenever it cannot be trusted.
export const WEBSITE_CAPTURE_PRELAUNCH = {
  loaded: false,
  launchState: 'prelaunch',
  salesEnabled: false,
  purchasable: false,
  price: 14.99,
  statusLabel: 'Available at launch',
  customerMessage:
    'Website Capture Suite is a paid VISINVOKE product. Pricing shown is the intended launch pricing; it becomes available to buy at VISINVOKE launch.',
};

function normalise(payload) {
  if (!payload || typeof payload !== 'object') return WEBSITE_CAPTURE_PRELAUNCH;
  // Only an explicit server `true` makes the product purchasable.
  const purchasable = payload.purchasable === true && payload.sales_enabled === true;
  return {
    loaded: true,
    launchState: typeof payload.launch_state === 'string' ? payload.launch_state : 'prelaunch',
    salesEnabled: payload.sales_enabled === true,
    purchasable,
    price: typeof payload.price === 'number' ? payload.price : WEBSITE_CAPTURE_PRELAUNCH.price,
    statusLabel:
      typeof payload.status_label === 'string' && payload.status_label
        ? payload.status_label
        : WEBSITE_CAPTURE_PRELAUNCH.statusLabel,
    customerMessage:
      typeof payload.customer_message === 'string' && payload.customer_message
        ? payload.customer_message
        : WEBSITE_CAPTURE_PRELAUNCH.customerMessage,
  };
}

/**
 * Read the authoritative Website Capture Suite launch state.
 * Returns the pre-launch shape until (and unless) the server says otherwise.
 */
export function useWebsiteCaptureLaunch() {
  const [state, setState] = useState(WEBSITE_CAPTURE_PRELAUNCH);

  useEffect(() => {
    let cancelled = false;
    API.get('/v1/website-capture/commerce')
      .then((res) => {
        if (!cancelled) setState(normalise(res && res.data));
      })
      .catch(() => {
        // Unreachable / errored -> stay pre-launch. Never assume availability.
        if (!cancelled) setState(WEBSITE_CAPTURE_PRELAUNCH);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/**
 * Start the REAL Website Capture Suite purchase. Mirrors the established paid
 * checkout shape: POST the plan to the server, which validates the sales gate and
 * Stripe configuration and returns a Stripe Checkout URL. This is only ever
 * reached from an ENABLED Buy control, which is itself gated by the server's
 * `purchasable` flag, so it cannot start a purchase while sales are disabled.
 */
export function startWebsiteCaptureCheckout() {
  // Return the raw request promise so PaidUtilitiesPage's shared guarded-checkout controller can
  // turn a server 409 into the acknowledgement flow and retry the exact same operation.
  return API.post('/v1/website-capture/checkout', { planId: 'standard' });
}

export default useWebsiteCaptureLaunch;
